import { Component, ViewChild, OnDestroy, OnInit, ChangeDetectorRef, DestroyRef, inject} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { allPermissions } from '@app/core/auth/permissions'
import { NavbarService } from '@app/core/layout/services/navbar.service'
import { FlattenedRequirement, RequirementFilters, RequirementSubmission } from '@app/models/requirement_submission'
import { TranslateService } from '@ngx-translate/core'
import { Subject, takeUntil, forkJoin, interval } from 'rxjs'
import { RequirementSubmissionsService } from './services/requirement-submissions.service'
import { ModalRequirementSubmissionFormComponent } from './modal-requirement-submission-form/modal-requirement-submission-form.component'
import { RequirementStatus } from '@app/shared/app.enums'
import { TranslateMessagesService } from '@app/shared/services/translate-messages.service'
import { environment } from 'src/config/environments/environment'

@Component({
  selector: 'app-requirement-submissions',
  templateUrl: './requirement-submissions.component.html',
  styleUrls: ['./requirement-submissions.component.css'],
  standalone: false,
})
export class RequirementSubmissionsComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef)
  refreshInterval$ = new Subject<void>()
  currentTabIndex = 0 // Track the current active tab

  constructor(
    private navbarService: NavbarService,
    public translate: TranslateService,
    private translateMessagesService: TranslateMessagesService,
    private requirementSubmissionsService: RequirementSubmissionsService,
    private cdr: ChangeDetectorRef
  ) {
    this.sysMessage = this.translateMessagesService.create('REQUIREMENTS')
  }

  model: RequirementSubmission = {} as RequirementSubmission
  @ViewChild('modalForm') modalForm: ModalRequirementSubmissionFormComponent

  loaded = false
  sysMessage: any
  requirementSubmissions: RequirementSubmission[] = []
  activeSubmissions: RequirementSubmission[] = []
  archivedSubmissions: RequirementSubmission[] = []
  flattenedArchivedRequirements: FlattenedRequirement[] = []
  filters: RequirementFilters = {}
  requirementOptions: { label: string; value: number }[] = []
  dateRange: Date[] = []
  sortField: string = 'id'
  sortOrder: number = -1

  // Pagination for Active Submissions
  activeCurrentPage: number = 1
  activeItemsPerPage: number = 10
  activeTotalRecords: number = 0
  activeTotalPages: number = 0

  // Pagination for Archived Submissions
  archivedCurrentPage: number = 1
  archivedItemsPerPage: number = 10
  archivedTotalRecords: number = 0
  archivedTotalPages: number = 0

  get NO_RESULTS_MESSAGE() {
    return this.sysMessage.system('NO_RESULTS_MESSAGE')
  }

  ngOnInit(): void {
    this.assignTitle()
    this.fetchRequirements()
    this.createRefreshInterval()

    this.requirementSubmissionsService.subscriber.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event.type === `UPDATE_${this.requirementSubmissionsService.id}`) {
        // Handle updated submission
        const updatedSubmission = event.data as RequirementSubmission
        this.onSubmissionChange(updatedSubmission)
      }
    })
  }

  ngOnDestroy() {
    this.stopRefreshInterval()
  }

  assignTitle() {
    this.navbarService.title.next(this.translate.instant('REQUIREMENT_SUBMISSIONS.TITLE'))
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.navbarService.title.next(this.translate.instant('REQUIREMENT_SUBMISSIONS.TITLE'))
    })
  }

  edit(event: Event, requirementSubmission: RequirementSubmission) {
    event.preventDefault()
    this.model = requirementSubmission
    this.modalForm.open(requirementSubmission)
  }

  viewDetails(event: Event, requirementSubmission: RequirementSubmission) {
    event.preventDefault()
    this.model = requirementSubmission
    this.modalForm.open(requirementSubmission)
  }

  viewRequirement(event: Event, requirementLog: FlattenedRequirement) {
    event.preventDefault()

    // Check if this is the newest log for this requirement
    const isNewestLog = this.isNewestLog(requirementLog)

    // Add the isNewestLog property to the requirement log object
    const requirementLogWithMetadata = {
      ...requirementLog,
      isNewestLog,
    }

    // We need to find the submission that contains this requirement ID
    const originalSubmission = this.archivedSubmissions.find((submission) =>
      submission.requirements?.some((req) => req.id === requirementLog.id && String(req.submission_id) === requirementLog.submission_id)
    )

    if (originalSubmission) {
      this.model = originalSubmission
      // Pass the enhanced requirement log object
      this.modalForm.open(originalSubmission, requirementLogWithMetadata, true)
    } else {
      console.error('Could not find matching submission for requirement:', requirementLog)
    }
  }

  isNewestLog(requirementLog: FlattenedRequirement): boolean {
    // Find all logs for the same requirement ID
    const sameRequirementLogs = this.flattenedArchivedRequirements.filter((log) => log.id === requirementLog.id)

    // Sort logs by creation date (newest first)
    sameRequirementLogs.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())

    // Check if the current log is the newest one
    return sameRequirementLogs.length > 0 && sameRequirementLogs[0].reviewed_expiration_date === requirementLog.reviewed_expiration_date
  }

  onTabChange(index: number) {
    this.loaded = false
    this.currentTabIndex = index
    this.cdr.detectChanges()

    // index is 0 for Active tab, 1 for Archive tab
    if (index === 0) {
      // Refresh active submissions with pagination
      this.fetchActiveSubmissions()

      // Start refresh interval for active tab
      this.createRefreshInterval()
    } else if (index === 1) {
      // Refresh archived submissions with pagination
      this.fetchArchivedSubmissions()

      // Stop refresh interval for archive tab
      this.stopRefreshInterval()
    }
  }

  onSubmissionChange(updatedSubmission: RequirementSubmission) {
    if (updatedSubmission.status === RequirementStatus.IN_REVIEW) {
      // Update or add to active submissions
      this.updateSubmissionInList(updatedSubmission, this.activeSubmissions)
      // Remove from archived if present
      this.removeSubmissionFromList(updatedSubmission, this.archivedSubmissions)
    } else {
      // Remove from active submissions
      this.removeSubmissionFromList(updatedSubmission, this.activeSubmissions)

      // refresh the entire archive data to ensure completeness
      this.fetchArchivedSubmissions()
    }
  }

  fetchRequirements() {
    this.loaded = false
    this.cdr.detectChanges()

    const activePageOptions = {
      page: this.activeCurrentPage,
      take: this.activeItemsPerPage,
    }

    const archivedPageOptions = {
      page: this.archivedCurrentPage,
      take: this.archivedItemsPerPage,
    }

    forkJoin({
      active: this.requirementSubmissionsService.get(activePageOptions),
      archived: this.requirementSubmissionsService.getArchived(undefined, archivedPageOptions),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.activeSubmissions = result.active.data
        this.archivedSubmissions = result.archived.data

        // Update active pagination
        if (result.active.pagination) {
          this.activeTotalRecords = result.active.pagination.totalCount
          this.activeTotalPages = result.active.pagination.totalPages
          this.activeCurrentPage = result.active.pagination.currentPage
        }

        // Update archived pagination
        if (result.archived.pagination) {
          this.archivedTotalRecords = result.archived.pagination.totalCount
          this.archivedTotalPages = result.archived.pagination.totalPages
          this.archivedCurrentPage = result.archived.pagination.currentPage
        }

        this.flattenArchivedRequirements()
        this.populateRequirementOptions()
        this.loaded = true
        this.cdr.detectChanges()
      })
  }

  flattenArchivedRequirements() {
    this.flattenedArchivedRequirements = []

    this.archivedSubmissions.forEach((submission) => {
      if (submission.requirements && Array.isArray(submission.requirements)) {
        submission.requirements.forEach((req) => {
          this.flattenedArchivedRequirements.push({
            id: req.id,
            title: req.title,
            type: req.type,
            submitted_data: req.submitted_data,
            expiration_date: req.expiration_date,
            reviewed_expiration_date: req.reviewed_expiration_date ? new Date(String(req.reviewed_expiration_date).split('T')[0]) : null,
            pass_id: String(submission.pass_id),
            pass_first_name: submission.pass_first_name,
            pass_last_name: submission.pass_last_name,
            user_full_name: req.user_full_name ?? '',
            created_at: submission.created_at,
            submission_id: String(req.submission_id),
            approved_at: req.approved_at,
          })
        })
      }
    })
  }

  updateSubmissionInList(submission: RequirementSubmission, list: RequirementSubmission[]) {
    const index = list.findIndex((item) => item.id === submission.id)
    if (index !== -1) {
      list[index] = submission
    } else {
      list.unshift(submission)
    }
  }

  removeSubmissionFromList(submission: RequirementSubmission, list: RequirementSubmission[] = this.activeSubmissions) {
    const index = list.findIndex((item) => item.id === submission.id)
    if (index !== -1) {
      list.splice(index, 1)
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case RequirementStatus.APPROVED:
        return 'text-success'
      case RequirementStatus.REJECTED:
        return 'text-danger'
      default:
        return ''
    }
  }

  get permissions() {
    return allPermissions
  }

  /**
   * Check if current language is RTL
   */
  get isRTL(): boolean {
    return this.translate.currentLang === 'he' || this.translate.currentLang === 'ar'
  }

  /**
   * Get localized pagination template for current language
   */
  get paginationTemplate(): string {
    return this.translate.instant('COMMON.PAGINATION.SHOWING')
  }

  onDateRangeSelect(_event: any) {
    // Reset date filters first
    this.filters.startDate = null
    this.filters.endDate = null

    // Apply only the selected dates
    if (this.dateRange && this.dateRange.length > 0) {
      // If first date is selected
      if (this.dateRange[0]) {
        this.filters.startDate = this.dateRange[0]
      }

      // If both dates are selected
      if (this.dateRange.length === 2 && this.dateRange[1]) {
        // Set end date to end of day
        const endDate = new Date(this.dateRange[1])
        endDate.setHours(23, 59, 59, 999)
        this.filters.endDate = endDate
      }
    }
  }

  onDateRangeClear() {
    this.filters.startDate = null
    this.filters.endDate = null
    this.dateRange = []
  }

  onSort(event: any) {
    this.sortField = event.field
    this.sortOrder = event.order
  }

  applyFilters() {
    this.loaded = false
    this.cdr.detectChanges()
    this.archivedCurrentPage = 1 // Reset to first page when applying filters

    // Construct filter object
    const apiFilters: any = {}

    // Only add properties that have valid values
    if (this.filters.phoneNumber) {
      apiFilters.phoneNumber = this.filters.phoneNumber
    }

    // Only add start date if it exists and is valid
    if (this.filters.startDate && this.isValidDate(this.filters.startDate)) {
      apiFilters.startDate = this.filters.startDate.toISOString()
    }

    // Only add end date if it exists and is valid
    if (this.filters.endDate && this.isValidDate(this.filters.endDate)) {
      const endDate = new Date(this.filters.endDate)
      endDate.setHours(23, 59, 59, 999)
      apiFilters.endDate = endDate.toISOString()
    }

    if (this.filters.requirementId) {
      apiFilters.requirementId = this.filters.requirementId
    }

    if (this.filters.expirationDate && this.isValidDate(this.filters.expirationDate)) {
      apiFilters.expirationDate = this.filters.expirationDate.toISOString()
    }

    const pageOptions = {
      page: this.archivedCurrentPage,
      take: this.archivedItemsPerPage,
    }

    // Call the service with filters and pagination
    this.requirementSubmissionsService
      .getArchived(apiFilters, pageOptions)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.archivedSubmissions = result.data

        if (result.pagination) {
          this.archivedTotalRecords = result.pagination.totalCount
          this.archivedTotalPages = result.pagination.totalPages
          this.archivedCurrentPage = result.pagination.currentPage
        }

        this.flattenArchivedRequirements()
        this.loaded = true
        this.cdr.detectChanges()
      })
  }

  // Helper method to verify date validity
  isValidDate(date: any): boolean {
    if (!date) return false

    // Check if it's a valid Date object
    if (date instanceof Date) {
      // Verify it's not the Unix epoch or invalid date
      return !isNaN(date.getTime()) && date.getFullYear() > 1970
    }

    return false
  }

  resetFilters() {
    this.filters = {}
    this.dateRange = []
    this.fetchRequirements()
  }

  populateRequirementOptions() {
    // Extract unique requirements for the filter dropdown
    const requirementMap = new Map()

    this.archivedSubmissions.forEach((submission) => {
      if (submission.requirements && Array.isArray(submission.requirements)) {
        submission.requirements.forEach((req) => {
          if (req.id && req.title && !requirementMap.has(req.id)) {
            requirementMap.set(req.id, {
              label: req.title,
              value: Number(req.id),
            })
          }
        })
      }
    })

    this.requirementOptions = Array.from(requirementMap.values())
  }

  get document() {
    return document
  }

  /**
   * Fetch active submissions with pagination
   */
  fetchActiveSubmissions(pageEvent?: any) {
    this.loaded = false
    this.cdr.detectChanges()

    if (pageEvent) {
      this.activeCurrentPage = Math.floor(pageEvent.first / pageEvent.rows) + 1
    }

    const pageOptions = {
      page: this.activeCurrentPage,
      take: this.activeItemsPerPage,
    }

    this.requirementSubmissionsService
      .get(pageOptions)!
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.activeSubmissions = result.data

        if (result.pagination) {
          this.activeTotalRecords = result.pagination.totalCount
          this.activeTotalPages = result.pagination.totalPages
          this.activeCurrentPage = result.pagination.currentPage
        }

        this.loaded = true
        this.cdr.detectChanges()
      })
  }

  /**
   * Fetch archived submissions with pagination
   */
  fetchArchivedSubmissions(pageEvent?: any) {
    this.loaded = false
    this.cdr.detectChanges()

    if (pageEvent) {
      this.archivedCurrentPage = Math.floor(pageEvent.first / pageEvent.rows) + 1
    }

    const pageOptions = {
      page: this.archivedCurrentPage,
      take: this.archivedItemsPerPage,
    }

    // Apply any active filters
    const apiFilters: any = {}
    if (this.filters.phoneNumber) {
      apiFilters.phoneNumber = this.filters.phoneNumber
    }
    if (this.filters.startDate && this.isValidDate(this.filters.startDate)) {
      apiFilters.startDate = this.filters.startDate.toISOString()
    }
    if (this.filters.endDate && this.isValidDate(this.filters.endDate)) {
      const endDate = new Date(this.filters.endDate)
      endDate.setHours(23, 59, 59, 999)
      apiFilters.endDate = endDate.toISOString()
    }
    if (this.filters.requirementId) {
      apiFilters.requirementId = this.filters.requirementId
    }
    if (this.filters.expirationDate && this.isValidDate(this.filters.expirationDate)) {
      apiFilters.expirationDate = this.filters.expirationDate.toISOString()
    }

    const hasFilters = Object.keys(apiFilters).length > 0

    this.requirementSubmissionsService
      .getArchived(hasFilters ? apiFilters : undefined, pageOptions)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.archivedSubmissions = result.data

        if (result.pagination) {
          this.archivedTotalRecords = result.pagination.totalCount
          this.archivedTotalPages = result.pagination.totalPages
          this.archivedCurrentPage = result.pagination.currentPage
        }

        this.flattenArchivedRequirements()
        this.populateRequirementOptions()
        this.loaded = true
        this.cdr.detectChanges()
      })
  }

  /**
   * Handle page change for active submissions
   */
  onActivePageChange(event: any) {
    this.fetchActiveSubmissions(event)
  }

  /**
   * Handle page change for archived submissions
   */
  onArchivedPageChange(event: any) {
    this.fetchArchivedSubmissions(event)
  }

  /**
   * Create refresh interval for active submissions tab
   */
  createRefreshInterval() {
    this.stopRefreshInterval()
    this.refreshInterval$ = new Subject()

    interval(environment.authorizedPeriod)
      .pipe(takeUntil(this.refreshInterval$))
      .subscribe(() => {
        // Only refresh if we're on the active submissions tab
        if (this.currentTabIndex === 0) {
          const pageOptions = {
            page: this.activeCurrentPage,
            take: this.activeItemsPerPage,
          }

          this.requirementSubmissionsService
            .get(pageOptions)!
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((result) => {
              this.activeSubmissions = result.data

              if (result.pagination) {
                this.activeTotalRecords = result.pagination.totalCount
                this.activeTotalPages = result.pagination.totalPages
              }
            })
        }
      })
  }

  /**
   * Stop refresh interval
   */
  stopRefreshInterval() {
    if (this.refreshInterval$) {
      this.refreshInterval$.next()
      this.refreshInterval$.complete()
    }
  }
}
